// src/services/useThumbnail.js
import { useState, useEffect } from 'react';
import thumbnailService from './thumbnailService';

/**
 * Hook for loading a single thumbnail
 * @param {number} contentId - The content ID to load thumbnail for
 * @returns {{ thumbnailUrl: string|null, loading: boolean, error: string|null }}
 */
export const useThumbnail = (contentId) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;

        const loadThumbnail = async () => {
            if (!contentId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const url = await thumbnailService.getThumbnail(contentId);

                if (isMounted) {
                    objectUrl = url;
                    setThumbnailUrl(url);
                    setLoading(false);
                }
            } catch (err) {
                if (isMounted) {
                    setError(err.message);
                    setLoading(false);
                }
            }
        };

        loadThumbnail();

        // Cleanup: revoke object URL when component unmounts or contentId changes
        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [contentId]);

    return { thumbnailUrl, loading, error };
};

/**
 * Hook for batch loading thumbnails
 * @param {number[]} contentIds - Array of content IDs to load thumbnails for
 * @returns {{ thumbnails: Object, loading: boolean, progress: Object }}
 */
export const useThumbnailBatch = (contentIds) => {
    const [thumbnails, setThumbnails] = useState({});
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    useEffect(() => {
        let isMounted = true;

        const loadThumbnails = async () => {
            if (!contentIds || contentIds.length === 0) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setProgress({ current: 0, total: contentIds.length });

            // First, trigger batch generation on server
            try {
                await thumbnailService.batchGenerateThumbnails(contentIds);
            } catch (err) {
                console.error('Batch generation failed:', err);
                // Continue anyway - individual fetches might still work
            }

            // Then load each thumbnail
            const loadedThumbnails = {};

            for (let i = 0; i < contentIds.length; i++) {
                const contentId = contentIds[i];

                try {
                    const url = await thumbnailService.getThumbnail(contentId);
                    if (isMounted) {
                        loadedThumbnails[contentId] = url;
                        setThumbnails({ ...loadedThumbnails });
                        setProgress({ current: i + 1, total: contentIds.length });
                    }
                } catch (err) {
                    console.error(`Failed to load thumbnail for ${contentId}:`, err);
                    loadedThumbnails[contentId] = null;
                }
            }

            if (isMounted) {
                setLoading(false);
            }
        };

        loadThumbnails();

        return () => {
            isMounted = false;
        };
    }, [JSON.stringify(contentIds)]); // Use JSON.stringify to deep compare array

    return { thumbnails, loading, progress };
};

export default useThumbnail;